// src/pages/SocialMedia.jsx
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PublicAPI } from '../api/public';
import { normalizeMediaUrl } from '../api/base';

const Wrap = styled.main` padding: 18px 0 24px; `;
const Container = styled.div` width:min(1400px,96vw); margin:0 auto; padding:0 18px; `;
const Title = styled.h1` margin:0 0 18px; `;
const Grid = styled.section`
  display:grid; gap:14px; grid-template-columns: repeat(4, 1fr);
  @media (max-width: 1200px){ grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 900px){ grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px){ grid-template-columns: 1fr; }
`;
const Card = styled.article`
  border:1px solid var(--bdr); border-radius:16px; background:var(--card); box-shadow:var(--shadow); overflow:hidden;
  display:flex; flex-direction:column;
  
  a.cover{ 
    display:block; 
    aspect-ratio: 16/10; 
    background:#e9eaec; 
    overflow: hidden;
    position: relative;
  }
  
  img{ 
    width:100%; 
    height:100%; 
    object-fit:cover; 
    object-position: center;
    display:block; 
  }
  
  .body{ padding:12px; display:flex; flex-direction:column; gap:8px; flex: 1; }
  .meta{ font-size:12px; color:var(--muted); }
  h3{ margin:0; font-size:16px; line-height:1.25; }
  p{ margin:0; color:var(--muted); font-size:13px; }
`;
const Pager = styled.div` display:flex; gap:8px; justify-content:center; margin-top:16px; `;
const Btn = styled.button`
  padding:8px 12px; border-radius:12px; border:1px solid var(--bdr); background:var(--card); box-shadow:var(--shadow);
  &[data-active="true"]{ background:var(--primary); color:var(--primaryFg); border-color:transparent; }
`;

const PAGE = 12;

export default function SocialMedia(){
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(1);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');

  React.useEffect(()=>{
    let cancelled = false;
    async function load(){
      setLoading(true); setErr('');
      try{
        // берём из публичного /socialmedia/posts (section=media)
        const data = await PublicAPI.socialMediaPosts({ page, page_size: PAGE, locale: i18n.language });
        const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
        setPosts(items);
        setTotal(Math.max(1, Math.ceil((data?.total ?? items.length)/PAGE)));
      }catch(e){
        // мягкий фоллбэк на pressList, если вдруг /socialmedia/posts не доступен
        try{
          const alt = await PublicAPI.pressList({ page, page_size: PAGE, locale: i18n.language });
          const altItems = Array.isArray(alt?.items) ? alt.items : (Array.isArray(alt) ? alt : []);
          if(!cancelled) {
            setPosts(altItems);
            setTotal(Math.max(1, Math.ceil((alt?.total ?? altItems.length)/PAGE)));
          }
        }catch(err2){
          if(!cancelled) setErr(e.message || err2.message || 'Error');
        }
      }
      finally{ if(!cancelled) setLoading(false); }
    }
    load(); return ()=>{ cancelled = true; };
  }, [page, i18n.language]);

  return (
    <Wrap>
      <Container>
        <Title>{t('socialmediaPage.title', 'СМИ о нас')}</Title>

        {err && <div style={{color:'#c62828', marginBottom:8}}>{err}</div>}
        {!loading && !posts.length && <div style={{opacity:.7}}>{t('common.nothingFound', 'Ничего не найдено')}</div>}

        <Grid aria-label="socialmedia-grid">
          {posts.map(x => (
            <Card key={x.slug}>
              <Link className="cover" to={`/socialmedia/${x.slug}`} aria-label={x.title}>
                {x.cover ? <img src={normalizeMediaUrl(x.cover)} alt={x.title}/> : null}
              </Link>
              <div className="body">
                <div className="meta">{x.date} {x.tag ? `• ${x.tag}`: ''}</div>
                <h3><Link to={`/socialmedia/${x.slug}`}>{x.title}</Link></h3>
                {x.excerpt && <p>{x.excerpt}</p>}
              </div>
            </Card>
          ))}
        </Grid>

        {total > 1 && (
          <Pager>
            {Array.from({length: total}).map((_,i)=>(
              <Btn key={i} data-active={page===i+1} onClick={()=>setPage(i+1)}>{i+1}</Btn>
            ))}
          </Pager>
        )}
      </Container>
    </Wrap>
  );
}
